import { action, autorun, observable, runInAction } from "mobx";
import { ethers } from "ethers";
import { tokens } from "../eth/contracts/contracts";
import { getContractByName, getTokenByName } from "./../eth/contracts/contracts";
import { getERC20balance } from "eth/methods/ERC20";
import EthStore from "./EthStore";
import handleTransaction from "helpers/handleTransaction";
import { formatResult } from "helpers/utils";
import { Token } from "types";

type TokenName = string;
type Spender = "Router" | "Vault" | "Governance";

type Allowance = {
  tokenName: TokenName;
  hasAllowance: boolean;
  poolId?: number;
};

class UserStore {
  @observable balances: Map<TokenName, number> = new Map();
  @observable allowances: Map<Spender, Allowance[]> = new Map();

  lastTimeFetched: number = 0;

  constructor() {
    autorun(this.syncToStorage);
  }

  syncToStorage = () => {
    if (localStorage) {
      localStorage.setItem("ygy-user-balances", JSON.stringify(this.balances));
      localStorage.setItem("ygy-allowances", JSON.stringify(this.allowances));
    }
  };

  @action initialize = async () => {
    this.fetchAllBalances();
    this.checkAllowances();
  };

  // Fetch token / eth balances
  @action fetchBalance = async (account: string, token: Token, provider: any) => {
    const balance = await getERC20balance(account, token, provider);
    runInAction(() => this.balances.set(token.name, balance));
  };

  @action checkAllowances = async () => {
    await Promise.all([
      await this.checkAllowance("YGY", "Router"),
      await this.checkAllowance("ETH", "Router"),
      await this.checkAllowance("LINK", "Router"),
      await this.checkAllowance("YGY/RAM", "Vault"),
      await this.checkAllowance("YGY", "Governance"),
      await this.checkAllowance("RAM", "Vault"),
    ]);
  };

  @action checkAllowance = async (tokenName: string, spenderName: Spender) => {
    if (tokenName === "ETH") return;
    const spender = getContractByName(spenderName, EthStore.network);
    const token = getTokenByName(tokenName, EthStore.network);
    if (spender && token) {
      const tokenContract = new ethers.Contract(token.address, token.abi, EthStore.signer);
      const result = await tokenContract.allowance(EthStore.account, spender.address);
      const parsedResult = formatResult(result);
      const allowance = {
        tokenName: token.name,
        hasAllowance: parsedResult > 0,
      };

      let allowances = this.allowances.get(spenderName);
      if (allowances) {
        const index = allowances.findIndex((allowance) => allowance.tokenName === tokenName);
        if (index === -1) {
          allowances.push(allowance);
        } else {
          allowances[index] = allowance;
        }
      } else {
        allowances = [allowance];
      }
      runInAction(() => this.allowances.set(spenderName, allowances!));
    }
    return;
  };

  @action getAllowance = async (tokenName: string, spenderName: Spender) => {
    const spender = getContractByName(spenderName, EthStore.network);
    const token = getTokenByName(tokenName, EthStore.network);
    if (token && spender) {
      const tokenContract = new ethers.Contract(token.address, token.abi, EthStore.signer);
      await handleTransaction(
        async () => await tokenContract.approve(spender.address, ethers.utils.parseEther("99999999999")),
        `approval of ${tokenName} for ${spenderName}`
      );
      const allowance = {
        tokenName,
        hasAllowance: true,
      };
      let allowances = this.allowances.get(spenderName);
      if (allowances) {
        const index = allowances.findIndex((allowance) => allowance.tokenName === tokenName);
        if (index === -1) {
          allowances.push(allowance);
          console.log("pushing", allowance, spenderName);
        } else {
          allowances[index] = allowance;
        }
      } else {
        allowances = [allowance];
      }
      runInAction(() => this.allowances.set(spenderName, allowances!));
      await this.checkAllowances();
    }
    return;
  };

  // Fetch all balances
  @action fetchAllBalances = async () => {
    const network = EthStore.network;
    const tokensForNetwork = tokens[network];
    // Eth balance
    const ethBalance = await EthStore.provider.getBalance(EthStore.account);
    this.balances.set("ETH", ethBalance / 1e18);
    // Token balances
    await Promise.all(tokensForNetwork.map(async (token) => await this.fetchBalance(EthStore.account, token, EthStore.provider)));
  };
}

export default new UserStore();
